import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpRight, BookOpen, Sparkles } from "lucide-react";
import BgLayer from "@/components/ui-shared/BgLayer";
import { LEARNING_CATEGORIES, ALL_COURSES } from "@/lib/learningTracks";

const LEVEL_STYLES = {
    Beginner:     { bg: "#E8FBEF", fg: "#1F7A3E" },
    Intermediate: { bg: "#FEF3E7", fg: "#B45309" },
    Advanced:     { bg: "#EEE8FF", fg: "#5B21B6" },
};

const CourseCard = ({ course, i }) => {
    const level = LEVEL_STYLES[course.level] || LEVEL_STYLES.Intermediate;
    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: (i % 12) * 0.03 }}
            className="group bg-white rounded-2xl shadow-[0_8px_28px_-12px_rgba(9,3,20,0.55)] hover:shadow-[0_18px_44px_-14px_rgba(161,0,255,0.35)] hover:-translate-y-1 transition-all duration-400 flex flex-col overflow-hidden"
            data-testid={`lt-course-card-${course.id}`}
        >
            {/* Image */}
            <div className="relative h-40 w-full overflow-hidden">
                <img
                    src={course.image}
                    alt={course.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{ background: `linear-gradient(135deg, ${course.hue}55 0%, transparent 60%)` }}
                />
            </div>

            {/* Body */}
            <div className="p-5 flex flex-col flex-1">
                <span
                    className="self-start inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-tight"
                    style={{ background: level.bg, color: level.fg }}
                    data-testid={`lt-course-level-${course.id}`}
                >
                    {course.level}
                </span>
                <h3 className="mt-3 font-display text-[#0E1220] text-[16px] leading-snug font-semibold tracking-tight min-h-[46px]">
                    {course.title}
                </h3>

                <button
                    type="button"
                    onClick={() => { if (window.__openCatalogue) window.__openCatalogue(); }}
                    data-testid={`lt-course-cta-${course.id}`}
                    className="mt-4 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#A100FF]/25 bg-[#F6EFFF] hover:bg-[#A100FF] text-[#7B00CC] hover:text-white font-display font-semibold text-[13px] py-2.5 transition-colors"
                >
                    Explore Program <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </motion.article>
    );
};

const LearningTracks = () => {
    const [searchParams] = useSearchParams();
    const [activeSlug, setActiveSlug] = useState(LEARNING_CATEGORIES[0]?.slug);
    const [query, setQuery] = useState(searchParams.get("q") || "");

    const isSearching = query.trim().length > 0;

    // Base list: searching -> ALL_COURSES, else -> selected category
    const displayed = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q) {
            return ALL_COURSES.filter(
                (c) => c.title.toLowerCase().includes(q) || c.category.toLowerCase().includes(q)
            );
        }
        const active = LEARNING_CATEGORIES.find((c) => c.slug === activeSlug);
        if (!active) return [];
        return active.courses.map((course) => ({
            ...course,
            category: active.name,
            categorySlug: active.slug,
            image: active.image,
            hue: active.hue,
        }));
    }, [activeSlug, query]);

    const activeCategory = LEARNING_CATEGORIES.find((c) => c.slug === activeSlug);

    return (
        <div data-testid="learning-tracks-page">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-12 overflow-hidden">
                <BgLayer variant="mesh" intensity="strong" />
                <div className="sxe-container relative">
                    <div className="max-w-3xl">
                        <div className="overline"><span className="dot" /> Learning Tracks</div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-6">
                            Build career-ready <span className="text-gradient">capability at scale</span>.
                        </h1>
                        <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6">
                            Nine enterprise-grade learning tracks — {ALL_COURSES.length}+ curated courses — mapped to the skills that move your business forward. Pick a track on the left to explore its programs.
                        </p>
                    </div>
                </div>
            </section>

            {/* Directory */}
            <section className="pb-28 relative">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-10">
                        {/* Sidebar */}
                        <aside className="lg:sticky lg:top-32 h-fit" data-testid="lt-sidebar">
                            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
                                <div className="px-3 py-2 overline"><span className="dot" /> Categories</div>
                                <nav className="mt-1 flex flex-col gap-1" role="tablist">
                                    {LEARNING_CATEGORIES.map((cat) => {
                                        const isActive = !isSearching && cat.slug === activeSlug;
                                        return (
                                            <button
                                                key={cat.slug}
                                                type="button"
                                                role="tab"
                                                aria-selected={isActive}
                                                onClick={() => { setQuery(""); setActiveSlug(cat.slug); }}
                                                data-testid={`lt-category-${cat.slug}`}
                                                className={`text-left px-4 py-3 rounded-xl font-display text-[14.5px] font-medium tracking-tight transition-all flex items-center justify-between gap-3 ${
                                                    isActive
                                                        ? "bg-gradient-to-r from-[#A100FF] to-[#7B00CC] text-white shadow-[0_10px_24px_-10px_rgba(161,0,255,0.6)]"
                                                        : "text-[#D8CDE8] hover:text-white hover:bg-white/[0.05]"
                                                }`}
                                            >
                                                <span>{cat.name}</span>
                                                <span className={`text-[11px] font-mono ${isActive ? "text-white/80" : "text-[#75668C]"}`}>
                                                    {cat.courses.length}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>
                        </aside>

                        {/* Main */}
                        <div>
                            {/* Header + search */}
                            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                                <div>
                                    <div className="overline"><span className="dot" /> {isSearching ? "Search results" : activeCategory?.name}</div>
                                    <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tighter leading-[1.1] text-white mt-3">
                                        {isSearching
                                            ? <>{displayed.length} <span className="text-gradient">course{displayed.length === 1 ? "" : "s"}</span> match &ldquo;{query}&rdquo;</>
                                            : <>Top <span className="text-gradient">{activeCategory?.name}</span> programs</>}
                                    </h2>
                                    {!isSearching && activeCategory?.blurb && (
                                        <p className="text-[#B0A3C4] text-[15px] leading-relaxed mt-2 max-w-2xl">
                                            {activeCategory.blurb}
                                        </p>
                                    )}
                                </div>

                                <div className="relative w-full md:w-80 shrink-0">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75668C]" />
                                    <input
                                        type="text"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search course or category..."
                                        data-testid="lt-search-input"
                                        className="w-full pl-11 pr-4 py-3 rounded-full bg-white/[0.04] border border-white/10 text-white placeholder-[#75668C] font-display text-sm focus:outline-none focus:border-[#C266FF]/50 transition-colors"
                                    />
                                </div>
                            </div>

                            {/* Meta strip */}
                            <div className="mb-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-[#B0A3C4] font-display">
                                <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#C266FF]" /> Instructor-led + AI-guided</div>
                                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C266FF]" /> Cohort or self-paced</div>
                                <div className="flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-[#C266FF]" /> OEM-aligned certification</div>
                            </div>

                            {/* Grid */}
                            {displayed.length === 0 ? (
                                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center">
                                    <p className="text-[#B0A3C4] font-display">No courses match &ldquo;{query}&rdquo;.</p>
                                </div>
                            ) : (
                                <AnimatePresence mode="popLayout">
                                    <motion.div
                                        key={isSearching ? `q-${query}` : activeSlug}
                                        layout
                                        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
                                        data-testid="lt-course-grid"
                                    >
                                        {displayed.map((course, i) => (
                                            <CourseCard key={course.id} course={course} i={i} />
                                        ))}
                                    </motion.div>
                                </AnimatePresence>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LearningTracks;
